import React, { useRef, useState } from 'react';
import { Box, Button, Checkbox, Divider, FormControlLabel, TextField, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import Tiptap from 'components/Tiptap/Tiptap'; // TipTap 컴포넌트
import { postNoticesCreateAPI } from 'apis/notice/noticeAPI'; // API 호출 함수
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SnackbarAlert from 'components/SnackbarAlert';
import { useTranslation } from 'react-i18next';
import { useSnackbarStore } from 'store/useSnackbarStore';
import { useNavigate } from 'react-router-dom';

export default function NoticeNew() {
  const [title, setTitle] = useState('');
  // const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [isMainPage, setIsMainPage] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const editorRef = useRef<any>(null);
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const navigate = useNavigate();

  // 스낵바
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  // 첨부파일 핸들러
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  // 저장버튼 핸들러
  const handleSubmit = async () => {
    if (!title.trim()) {
      showSnackbar(t('notice.new.warn_title'), 'warning');
      return;
    }
    if (isMainPage && !endDate) {
      showSnackbar(t('notice.new.warn_date'), 'warning');
      return;
    }

    // 본문 내용 가져오기
    const contentHTML = editorRef?.current?.getHTML() || '';
    setContent(contentHTML);

    // 파일 처리 (첨부파일이 있는 경우)
    let fileAttachment = null;
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        fileAttachment = fileReader.result as string; // base64로 변환
      };
      fileReader.readAsDataURL(file);
    }

    // API 호출 데이터 생성
    const requestData = {
      title,
      content: contentHTML,
      language: i18n.language,
      isFixed: isFixed,
      isHidden: false, // 기본값 설정 (*추후에 필요할수도있을듯 일단 냅두고 false로 던짐)
      isPopup: isMainPage, // 메인 노출 여부에 따라 팝업 설정
      popupExpiredAt: isMainPage ? dayjs(endDate).format('YYYY-MM-DD') : null,
      attachments: fileAttachment || null
    };

    try {
      // API 호출
      const response = await postNoticesCreateAPI(requestData);
      if (response) showSnackbar(t('notice.new.success_save'), 'success');
      navigate('/notice'); // 공지사항 페이지로 이동
    } catch (error) {
      showSnackbar(t('notice.new.error_save'), 'error');
    }
  };

  return (
    <Box
      padding={2}
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{
        width: { xs: '100vw', sm: '100%' },
        justifyContent: 'center'
      }}
    >
      <SnackbarAlert />

      <Box
        borderRadius={1}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="h5" fontWeight="bold">
          {t('notice.new.title')}
        </Typography>

        <Divider flexItem />
      </Box>

      <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="column" gap={1} padding={2} borderRadius={1} boxShadow={1} bgcolor={theme.palette.contentBlock.main}>
          {/* 제목 */}
          <Box display="flex" sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'start' } }} gap={1}>
            <Typography sx={{ width: { xs: '100%', sm: '90px' } }} variant="caption" height={32} lineHeight={2.75}>
              {t('notice.new.table.title')}
            </Typography>
            <TextField
              required
              size="small"
              placeholder={t('notice.new.table.title_placeholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{
                flexGrow: 1,
                '& .MuiInputBase-root': {
                  height: '32px',
                  borderRadius: '4px',
                  '& input': { padding: '0 8px' }
                }
              }}
            />
          </Box>
          {/* 상단고정(중요) */}
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography width={90} variant="caption" height={32} lineHeight={2.75}>
              {t('notice.new.table.fixed')}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={isFixed} onChange={() => setIsFixed(!isFixed)} />}
              label={t('notice.new.table.fixed_checkbox')}
              sx={{
                transform: 'scale(0.8)', // 크기 축소
                padding: '2px' // 내부 패딩 조정
              }}
            />
          </Box>
          {/* 메인 노출설정 */}
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography width={90} variant="caption" height={32} lineHeight={2.75}>
              {t('notice.new.table.popup')}
            </Typography>
            <FormControlLabel
              control={<Checkbox checked={isMainPage} onChange={() => setIsMainPage(!isMainPage)} />}
              label={t('notice.new.table.popup_checkbox')}
              sx={{
                transform: 'scale(0.8)', // 크기 축소
                padding: '2px' // 내부 패딩 조정
              }}
            />
            {/* 종료 날짜 */}
            <Box display="flex" gap={2}>
              <DatePicker
                disabled={!isMainPage}
                format="YYYY-MM-DD"
                value={endDate}
                onChange={(newValue: any) => setEndDate(newValue)}
                minDate={dayjs()}
                slotProps={{
                  calendarHeader: {
                    format: 'YYYY-MM'
                  },
                  textField: { placeholder: t('notice.new.table.date_placeholder') }
                }}
                sx={{
                  flexGrow: 1,
                  width: '140px',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem' // 아이콘 크기 조정
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 본문 */}
        <Box
          gap={1}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'start' }
          }}
        >
          <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.main} width="100%">
            <Tiptap ref={editorRef} content={content} editable={true} minHeight="300px" maxHeight="400px" />
          </Box>
        </Box>

        {/* 첨부 파일 */}
        {/* <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <Typography width={90} variant="caption" height={32} lineHeight={2.75}>
            첨부파일
          </Typography>
          <Button variant="contained" component="label" startIcon={<AttachFileIcon />}>
            파일 선택
            <input type="file" hidden onChange={handleFileChange} />
          </Button>
          {file && <Typography>{file.name}</Typography>}
        </Box> */}
      </Box>

      {/* 저장 버튼 */}
      <Box display="flex" justifyContent="flex-start">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{
            width: '100px',
            borderRadius: '4px'
          }}
        >
          {t('notice.new.save')}
        </Button>
      </Box>
    </Box>
  );
}
